<script setup lang="ts">
/**
 * App Header Component — Donor Portal
 *
 * Main application header with navigation and user menu
 */
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { Menu as MenuIcon, ChevronDown, Search, Bell, Sun, Globe, LogOut } from 'lucide-vue-next';
import InputText from 'primevue/inputtext';
import { useAuthStore, useUIStore } from '@/stores';
import { useLocale } from '@/shared/composables/useLocale';
import logoImage from '@/assets/timeless-medical-logo.png';

const authStore = useAuthStore();
const uiStore = useUIStore();
const { availableLanguages, locale: currentLanguage, setLanguage, isLoading: isLangLoading, t } = useLocale();

// Search state
const isSearchExpanded = ref(false);
const searchQuery = ref('');

function toggleSearch() {
  isSearchExpanded.value = !isSearchExpanded.value;
  if (!isSearchExpanded.value) {
    searchQuery.value = '';
  }
}

function toggleDesktopSidebar() {
  uiStore.toggleSidebarCollapse();
}

const userInitials = computed(() => {
  const name = authStore.userName || '';
  if (!name.trim()) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
});

function toggleSidebar() {
  uiStore.toggleSidebar();
}
</script>

<template>
  <header class="custom-header sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-4">
    <!-- Mobile menu button -->
    <button
      type="button"
      class="-m-2.5 p-2.5 text-gray-700 lg:hidden"
      @click="toggleSidebar"
    >
      <span class="sr-only">{{ t('nav.openSidebar') }}</span>
      <MenuIcon
        class="h-6 w-6"
        aria-hidden="true"
      />
    </button>

    <!-- Desktop sidebar toggle button -->
    <button
      type="button"
      class="hidden lg:flex -m-2.5 p-2.5 text-gray-700 hover:text-primary-600 transition-colors items-center"
      :title="uiStore.sidebarCollapsed ? t('nav.showSidebar') : t('nav.hideSidebar')"
      @click="toggleDesktopSidebar"
    >
      <MenuIcon
        class="h-6 w-6"
        aria-hidden="true"
      />
    </button>

    <!-- Logo -->
    <RouterLink
      to="/"
      class="flex items-center"
    >
      <img
        :src="logoImage"
        alt="Timeless Medical"
        class="h-8 w-auto"
      >
    </RouterLink>

    <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
      <!-- Spacer -->
      <div class="flex flex-1" />

      <!-- Right-side controls -->
      <div class="flex items-center gap-x-4 lg:gap-x-4">
        <!-- Expandable Search (future scope) -->
        <!-- <div class="flex items-center gap-x-2">
          <button
            type="button"
            class="flex items-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            :title="isSearchExpanded ? 'Close search' : 'Search'"
            @click="toggleSearch"
          >
            <Search
              class="h-5 w-5 text-gray-700 dark:text-gray-300"
              aria-hidden="true"
            />
          </button>

          <transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="w-0 opacity-0"
            enter-to-class="w-64 opacity-100"
            leave-active-class="transition-all duration-300 ease-in"
            leave-from-class="w-64 opacity-100"
            leave-to-class="w-0 opacity-0"
          >
            <InputText
              v-if="isSearchExpanded"
              v-model="searchQuery"
              placeholder="Search..."
              class="block rounded-lg border border-gray-300 bg-white py-2 px-3 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400"
              autofocus
            />
          </transition>
        </div> -->

        <!-- Theme toggle (future scope) -->
        <!-- <button
          type="button"
          class="flex items-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title="Theme"
        >
          <Sun
            class="h-5 w-5 text-gray-700 dark:text-gray-300"
            aria-hidden="true"
          />
        </button> -->

        <!-- Country selector -->
        <Menu
          as="div"
          class="relative"
        >
          <MenuButton
            class="flex items-center gap-x-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isLangLoading"
          >
            <Globe
              class="h-5 w-5 text-gray-500 shrink-0"
              aria-hidden="true"
            />
            <span class="hidden lg:inline">{{ currentLanguage }}</span>
            <ChevronDown
              class="h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
          </MenuButton>
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <MenuItem
                v-for="lang in availableLanguages"
                :key="lang.code"
                v-slot="{ active }"
              >
                <button
                  type="button"
                  :class="[
                    active ? 'bg-gray-100' : '',
                    currentLanguage === lang.code ? 'bg-gray-50 font-semibold' : '',
                    'flex w-full items-center gap-x-3 px-4 py-2 text-sm text-gray-700'
                  ]"
                  @click="setLanguage(lang.code)"
                >
                  <Globe
                    class="h-4 w-4 text-gray-400 shrink-0"
                    aria-hidden="true"
                  />
                  <div class="flex flex-col items-start">
                    <span>{{ lang.nativeName }}</span>
                    <span class="text-xs text-gray-500">{{ lang.code }}</span>
                  </div>
                </button>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>

        <!-- Notifications (future scope) -->
        <!-- <button
          type="button"
          class="relative flex items-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title="Notifications"
        >
          <Bell
            class="h-6 w-6 text-gray-700 dark:text-gray-300"
            aria-hidden="true"
          />
        </button> -->

        <!-- User Profile -->
        <Menu
          v-if="authStore.userName"
          as="div"
          class="relative"
        >
          <MenuButton class="flex items-center gap-x-2.5 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-gray-100 transition-colors focus:outline-none">
            <span class="flex items-center justify-center h-7 w-7 rounded-full bg-blue-600 text-white text-xs font-semibold shadow-sm">
              {{ userInitials }}
            </span>
            <span class="text-sm font-semibold text-gray-900">
              {{ authStore.userName }}
            </span>
          </MenuButton>
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <MenuItem v-slot="{ active }">
                <button
                  type="button"
                  :class="[
                    active ? 'bg-gray-100' : '',
                    'flex w-full items-center gap-x-3 px-4 py-2 text-sm text-gray-700'
                  ]"
                  @click="authStore.logout()"
                >
                  <LogOut
                    class="h-4 w-4 text-gray-400 shrink-0"
                    aria-hidden="true"
                  />
                  {{ t('auth.logout') }}
                </button>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>
  </header>
</template>

<style scoped>
.custom-header {
  background: linear-gradient(0deg, rgba(228, 247, 251, 0.20) 100%, rgba(255, 255, 255, 0.20) 100%, rgba(228, 247, 251, 0.20) 100%), white;
}
</style>
